// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { HttpErrorResponse } from '@angular/common/http';
import 'reflect-metadata';
import { BehaviorSubject, Observable, ReplaySubject, of } from 'rxjs';
import {
  catchError,
  finalize,
  map,
  shareReplay,
  take,
  tap,
} from 'rxjs/operators';

/**
 * DO NOT USE THIS DIRECTLY
 * use MapApiService(.service.ts) instead!
 */

export const UNKEYED_API = 'unkeyed' as const;
export const STATIC_API = 'static' as const;

export interface RequestOptions {
  /**
   * if true, previous data in data$ will be set to undefined before
   * a new request is invoked
   * does NOT affect lastSuccessfulData$
   * default: true
   */
  clearStaleData?: boolean;

  /**
   * if true, a new request will set error$ undefined again
   * does NOT affect lastSuccessfulData$
   * default: true
   */
  clearStaleError?: boolean;

  /**
   * force refresh even if params have not changed
   */
  forceRefresh?: boolean;
}

export const defaultRequestOptions: Required<RequestOptions> = {
  clearStaleData: true,
  clearStaleError: true,
  forceRefresh: false,
};

export type ChangeInfo<T = unknown> =
  | {
      same: false;
      old: DeepReadonly<T>;
      new: DeepReadonly<T>;
      url: string;
    }
  | {
      same: true;
      url: string;
    };

export type VoidIfEmpty<T, IfNotEmpty = T> = [keyof T] extends [never]
  ? void
  : IfNotEmpty;

export function paramObjectKeyerDefault<TParams>(params?: TParams): string {
  return params ? JSON.stringify(params) : UNKEYED_API;
}

// https://dev.to/bwca/deep-readonly-generic-in-typescript-4b04
// use this to enforce nested datastructures to be read only
export type DeepReadonly<T> = Readonly<{
  [K in keyof T]: T[K] extends Date | number | string | symbol
    ? Readonly<T[K]>
    : T[K] extends Array<infer A>
    ? Readonly<Array<DeepReadonly<A>>>
    : DeepReadonly<T[K]>;
}>;

export class APICallState<
  TParams,
  TData,
  TParamsPossiblyVoid extends VoidIfEmpty<TParams> = VoidIfEmpty<TParams>
  // TDataReadOnly = TData extends any[] ? ReadonlyArray<TData>
> {
  public lastParams?: TParamsPossiblyVoid;

  /**
   * returns an (readonly) version of the current data.
   * MAY TRANSITION TO 'cleared' at any time. This happens in case, after a successful request,
   * another request was made with options 'clearStale: true' or 'clearOnFailure: true'.
   */
  public data$: ReplaySubject<DeepReadonly<TData | undefined>> =
    new ReplaySubject(1);

  /**
   * returns an (readonly) version of LAST SUCCESSFULLY RETRIEVED DATA.
   * Once it has data the data never gets removed unless it is overwritten with new data.
   * So it only gets updated on successful requests (i.e. is not cleared on
   * when invoking .request() with options 'clearStale: true' or 'clearOnFailure: true')
   */
  public lastSuccessfulData$: ReplaySubject<DeepReadonly<TData>> =
    new ReplaySubject(1);

  /**
   *
   */
  public lastSuccessfulRetry$: ReplaySubject<ChangeInfo<TData>> =
    new ReplaySubject(1);

  /**
   * fires true in case a request is ongoing
   * false otherwise. tells you nothing about whether the request succeeded
   * or failed, only whether it is in transit
   */
  public loading$ = new BehaviorSubject<boolean>(false);

  /**
   * in case a loading cycle was triggered by a retry
   * this observable will be true during the request
   */
  public retrying$ = new BehaviorSubject<boolean>(false);

  /**
   * fires when there is an error
   * when a new request is issued this i
   */
  public error$ = new ReplaySubject<HttpErrorResponse | undefined>(1);

  private _error = undefined;

  /**
   * key of last request
   */
  private cacheKey = '';

  /**
   * resets all data except lastSuccessfulData$
   */
  public reset(): void {
    this.cacheKey = ''; // important to trash cache key as well!
    this.data$.next(undefined); // leave lastSuccessfulData$ as is
    this.loading$.next(false);
    if (this._error) {
      this._error = undefined;
      this.error$.next(this._error);
    }
  }

  private makeKey(params: TParamsPossiblyVoid): string {
    return typeof this.keyer === 'string' ? this.keyer : this.keyer(params);
  }

  /**
   * Executes a request task
   * @param params parameters for request
   * @param keyer function to map parameters to a string (for keying), if your key is primitive data it will use JSON.stringify by default
   * @returns
   */
  public request(
    params: TParamsPossiblyVoid,
    options: RequestOptions = defaultRequestOptions
  ): Observable<DeepReadonly<TData>> {
    const { clearStaleData, clearStaleError, forceRefresh } = {
      ...defaultRequestOptions,
      ...options,
    };

    // compute key to see if we need to un a new request
    const key = this.makeKey(params);
    if (!forceRefresh && key !== UNKEYED_API && this.cacheKey === key) {
      // requests are lazy
      return this.lastSuccessfulData$;
    }

    if (clearStaleData) {
      // lastSuccessfulData$ stays!
      this.data$.next(undefined);
    }

    if (clearStaleError) {
      if (this._error) {
        this._error = undefined;
        this.error$.next(this._error);
      }
    }

    this.cacheKey = key;
    this.loading$.next(true);
    this.lastParams = params; // store for retry
    const req = this._request(params as unknown as TParams).pipe(
      catchError((error) => {
        this.cacheKey = '';
        this._error = error;
        this.error$.next(error);
        this.retrying$.next(false);
        // we will continue with undefined
        return of(undefined);
      }),
      finalize(() => {
        if (this.retrying$.value) {
          this.retrying$.next(false);
        }
        this.loading$.next(false);
      }),
      shareReplay(),
      take(1)
    );
    req.subscribe((data) => {
      if (data) {
        // only successful requests here
        this.lastSuccessfulData$.next(data as DeepReadonly<TData>);
      }
      this.data$.next(data as DeepReadonly<TData>);
    });
    return req as Observable<DeepReadonly<TData>>;
  }

  /**
   * retry with last parameters
   */
  public retry(
    options?: RequestOptions
  ): ReturnType<typeof this.request> | undefined {
    this.retrying$.next(true);
    return this.lastParams ? this.request(this.lastParams, options) : undefined;
  }

  /**
   * retry with last parameters
   */
  public retryAndDetectChange(
    options?: RequestOptions
  ): Observable<ChangeInfo<TData>> | undefined {
    let prevData: DeepReadonly<TData>;
    this.lastSuccessfulData$
      .subscribe((data) => (prevData = data))
      .unsubscribe();
    this.retrying$.next(true);
    const noChange = { same: true, url: this.URL } as ChangeInfo<TData>;
    this.lastSuccessfulRetry$.next(noChange);
    return this.lastParams
      ? this.request(this.lastParams, options).pipe(
          map(
            (data) =>
              (JSON.stringify(data) === JSON.stringify(prevData)
                ? {
                    same: true,
                    url: this.URL,
                  }
                : {
                    same: false,
                    old: prevData as TData,
                    new: data as TData,
                    url: this.URL,
                  }) as ChangeInfo<TData>
          ),
          tap((change) => {
            this.lastSuccessfulRetry$.next(change);
          })
        )
      : of(noChange);
  }

  /**
   * explicitly set data
   * loading state and error state will be reset!
   * @param data payload
   */
  set(data: DeepReadonly<TData>, paramsForKey?: TParamsPossiblyVoid): void {
    this.cacheKey = paramsForKey ? this.makeKey(paramsForKey) : UNKEYED_API;
    this.data$.next(data);
    this.lastSuccessfulData$.next(data);
    this.loading$.next(false);
    this._error = undefined;
    this.error$.next(this._error);
  }

  constructor(
    private _request: (params: TParams) => Observable<TData>,
    private keyer:
      | typeof UNKEYED_API
      | typeof STATIC_API
      | ((params: TParamsPossiblyVoid) => string) = paramObjectKeyerDefault,
    public readonly URL: string = ''
  ) {
    this.reset();
  }
}
